// Mocked reference-image data for the ideation Search tab. The category cover
// thumbnails reuse the Figma design assets; the in-grid results are generated
// placeholders that cycle the five aspect-ratio variations so the masonry
// gallery (and the Edit canvas) can be exercised against every shape until the
// real search backend is wired up.
import aircraft from 'images/three_d_studio/aircraft.jpg'
import animals from 'images/three_d_studio/animals.jpg'
import car from 'images/three_d_studio/car.jpg'
import character from 'images/three_d_studio/character.jpg'
import generated1 from 'images/three_d_studio/generated-1.jpg'

// The aspect-ratio variations a generated image can take, with representative
// pixel dimensions so each placeholder carries the correct intrinsic ratio.
export const IMAGE_VARIATIONS = [
  { ratio: '1:1', width: 1024, height: 1024 },
  { ratio: '2:3', width: 832, height: 1216 },
  { ratio: '3:2', width: 1216, height: 832 },
  { ratio: '9:16', width: 768, height: 1344 },
  { ratio: '16:9', width: 1344, height: 768 },
]

const VARIATION_COLORS = ['#1f6feb', '#8957e5', '#2da44e', '#bf3989', '#d29922', '#0b8c8c']

// A self-contained SVG data URI sized to the given dimensions (no network), so
// the image's intrinsic aspect ratio drives the masonry/canvas layout.
const placeholderUrl = (width, height, label, background) => {
  const fontSize = Math.round(Math.min(width, height) / 6)
  const dimensions = `width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"`
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" ${dimensions}>`
    + `<rect width="100%" height="100%" fill="${background}"/>`
    + `<text x="50%" y="50%" fill="#ffffff" font-family="Arial, sans-serif" font-size="${fontSize}"`
    + ` font-weight="600" text-anchor="middle" dominant-baseline="middle">${label}</text>`
    + '</svg>'
  return `data:image/svg+xml,${encodeURIComponent(svg)}`
}

// Cycles the variations and colour palette so a sequence of images exercises
// every aspect ratio. Returns the fields shared by every mocked image.
const variationImage = (index) => {
  const variation = IMAGE_VARIATIONS[index % IMAGE_VARIATIONS.length]
  const background = VARIATION_COLORS[index % VARIATION_COLORS.length]
  return {
    url: placeholderUrl(variation.width, variation.height, variation.ratio, background),
    aspectRatio: variation.ratio,
  }
}

const CATEGORY_DEFS = [
  { value: 'aircraft', name: 'Aircraft', cover: aircraft },
  { value: 'animals', name: 'Animals', cover: animals },
  { value: 'architectural', name: 'Architectural', cover: character },
  { value: 'exterior', name: 'Exterior', cover: car },
  { value: 'interior', name: 'Interior', cover: aircraft },
  { value: 'car', name: 'Car', cover: car },
  { value: 'character', name: 'Character', cover: character },
  { value: 'military', name: 'Military', cover: aircraft },
  { value: 'furniture', name: 'Furniture', cover: animals },
  { value: 'industrial', name: 'Industrial', cover: car },
  { value: 'art-design', name: 'Art & Design', cover: character },
  { value: 'home-living', name: 'Home & Living', cover: animals },
]

const buildImages = (categoryIndex, name) =>
  Array.from({ length: 9 }, (_, i) => ({
    id: (categoryIndex + 1) * 100 + i + 1,
    ...variationImage(categoryIndex + i),
    alt: `${name} reference ${i + 1}`,
  }))

export const SEARCH_CATEGORIES = CATEGORY_DEFS.map((category, index) => ({
  ...category,
  images: buildImages(index, category.name),
}))

// Results returned for any successful mock search; enough to fill the masonry
// gallery and cycle through every aspect-ratio variation.
const SEARCH_RESULTS = Array.from({ length: 12 }, (_, index) => ({
  id: 9000 + index,
  ...variationImage(index),
  alt: `Search result ${index + 1}`,
}))

const SEARCH_DELAY_MS = 2000

/**
 * Mocked image search until the real backend is wired up. Resolves after a
 * short delay: "car" returns no results; any other term returns the first four.
 * @param {string} term - The search term
 * @returns {Promise<Array<{ id: number, url: string, alt: string }>>}
 */
export const mockSearchImages = term => new Promise((resolve) => {
  setTimeout(() => {
    resolve(term.trim().toLowerCase() === 'car' ? [] : SEARCH_RESULTS)
  }, SEARCH_DELAY_MS)
})

// Quick-action tags shown in the Create composer; the set depends on how many
// reference images have been attached.
export const CREATE_TAGS = {
  SINGLE: ['Generate variation', 'Reuse style', 'Reuse character', 'Create front, side, and back views'],
  MULTI: ['Generate variation', 'Combine styles', 'Combine elements'],
}

// Images cycled as mocked generation outputs (the first is the Figma result render).
const GENERATED_POOL = [generated1, car, aircraft, character]
let generationCount = 0

/**
 * Mocked image generation until the real backend is wired up. Resolves after a
 * short delay with an outcome: 1 in every 4 attempts fails (~25%), starting with
 * the first so a send still surfaces the failed state for QA; the rest succeed,
 * cycling through GENERATED_POOL so repeated generations return different images.
 * @returns {Promise<{ failed: boolean, image?: { id: number, url: string, alt: string } }>}
 */
export const mockGenerateImage = () => new Promise((resolve) => {
  setTimeout(() => {
    generationCount += 1
    if (generationCount % 4 === 1) {
      resolve({ failed: true })
      return
    }
    const url = GENERATED_POOL[generationCount % GENERATED_POOL.length]
    resolve({ failed: false, image: { id: 20000 + generationCount, url, alt: 'Generated image' } })
  }, SEARCH_DELAY_MS)
})

let editCount = 0

/**
 * Mocked inpainting edit until the real backend is wired up. Always resolves with
 * an edited image (the Edit tab has no failed state) after a short delay, cycling
 * GENERATED_POOL so repeated edits return different images.
 * @returns {Promise<{ id: number, url: string, alt: string }>}
 */
export const mockEditImage = () => new Promise((resolve) => {
  setTimeout(() => {
    editCount += 1
    const url = GENERATED_POOL[editCount % GENERATED_POOL.length]
    resolve({ id: 30000 + editCount, url, alt: 'Edited image' })
  }, SEARCH_DELAY_MS)
})
